/**
 * HOOK: useCRUD
 * Hook générique pour gérer les opérations CRUD sur n'importe quelle table Supabase
 *
 * @example
 * // Usage simple
 * const { data, loading, create, update, remove } = useCRUD<Product>('products');
 *
 * @example
 * // Usage avec relations et transformation
 * const { data, loading } = useCRUD<Contact>('contacts', {
 *   select: '*, products:contact_products(product:products(id, name))',
 *   transform: (raw) => ({
 *     ...raw,
 *     products: raw.products?.map((cp: any) => cp.product).filter(Boolean) || [],
 *   }),
 * });
 */

import { useEffect, useState, useCallback } from 'react';
import { useSupabase } from './useSupabase';
import type {
  BaseEntity,
  UseCRUDOptions,
  UseCRUDReturn,
  CRUDError,
  QueryParams,
  PaginatedResult,
} from '../types/crud';
import { logEntityUpdate, logAssignedUserChange } from '../lib/timeline-logger';
import { monitorQuery } from '../lib/supabase-monitor';
import { applyFiltersToQuery } from '../lib/supabase-filters';
import { buildSearchOrConditions } from '../lib/postgrestSearch';

export function useCRUD<
  T extends BaseEntity,
  TCreate = Partial<T>,
  TUpdate = Partial<T>,
  TRaw = any,
>(tableName: string, options: UseCRUDOptions<T, TRaw> = {}): UseCRUDReturn<T, TCreate, TUpdate> {
  const supabase = useSupabase();
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(!options.manual);
  const [error, setError] = useState<Error | null>(null);

  const {
    select = '*',
    orderBy = { column: 'created_at', ascending: false },
    transform,
    transformInput,
    manual = false,
    onFetchSuccess,
    afterFetch,
    onError,
    searchableFields = [],
  } = options;

  /**
   * Créer une erreur enrichie à partir d'une erreur Supabase
   */
  function createCRUDError(message: string, originalError?: any, operation?: string): CRUDError {
    const crudError = new Error(message) as CRUDError;

    if (originalError) {
      crudError.code = originalError.code;
      crudError.details = originalError.details;
      crudError.hint = originalError.hint;
      crudError.originalError = originalError;
    }

    if (operation) {
      crudError.message = `[${operation}] ${message}`;
    }

    return crudError;
  }

  /**
   * Gérer les erreurs de manière centralisée
   */
  const handleError = useCallback(
    (err: any, operation: string): void => {
      const crudError = createCRUDError(
        err.message || `Erreur lors de l'opération ${operation}`,
        err,
        operation
      );

      console.error(`[useCRUD:${tableName}:${operation}] Erreur:`, crudError);
      setError(crudError);

      if (onError) {
        onError(crudError);
      }
    },
    [tableName, onError]
  );

  /**
   * Récupérer toutes les entités
   * Retourne les données chargées pour utilisation immédiate
   */
  const refresh = useCallback(async (): Promise<T[]> => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase.from(tableName).select(select);

      // Appliquer le tri
      if (orderBy) {
        query = query.order(orderBy.column, { ascending: orderBy.ascending ?? false });
      }

      // 📊 MONITORED: Récupération de toutes les entités
      const { data: rawData, error: fetchError } = await monitorQuery(
        tableName,
        'select',
        `all with relations (${select})`,
        (() => query) as any,
        `useCRUD(${tableName}).refresh`
      );

      if (fetchError) {
        throw fetchError;
      }

      // Transformer les données si nécessaire
      let transformedData =
        transform && rawData
          ? (rawData as any[]).map((item: any) => transform(item))
          : (rawData as unknown as T[]) || [];

      // Appliquer le hook afterFetch si présent
      if (afterFetch) {
        transformedData = await afterFetch(transformedData);
      }

      setData(transformedData);

      if (onFetchSuccess) {
        onFetchSuccess(transformedData);
      }

      return transformedData;
    } catch (err) {
      handleError(err, 'refresh');
      return [];
    } finally {
      setLoading(false);
    }
  }, [supabase, tableName, select, orderBy, transform, afterFetch, onFetchSuccess, handleError]);

  /**
   * Récupérer les entités avec pagination server-side
   */
  const fetchPaginated = useCallback(
    async (params: QueryParams): Promise<PaginatedResult<T>> => {
      try {
        setLoading(true);
        setError(null);

        const {
          page = 1,
          limit = 10,
          search = '',
          sort,
          filters,
          filterMode = 'and',
          requiredFilters,
        } = params;

        // Construction de la requête avec count
        let query = supabase.from(tableName).select(select, { count: 'exact', head: false });

        // Recherche globale sur les champs searchable
        if (search && searchableFields && searchableFields.length > 0) {
          query = query.or(buildSearchOrConditions(searchableFields as string[], search));
        }

        // Appliquer les filtres avancés avec le mode ET/OU
        // Les requiredFilters s'appliquent toujours en mode AND (ex: filtre d'onglet)
        if ((filters && filters.length > 0) || (requiredFilters && requiredFilters.length > 0)) {
          query = applyFiltersToQuery(query, filters || [], filterMode, requiredFilters || []);
        }

        // Tri
        const sortColumn = sort?.field || orderBy?.column || 'created_at';
        const sortAscending = sort?.direction === 'asc' ? true : (orderBy?.ascending ?? false);
        query = query.order(sortColumn, { ascending: sortAscending });

        // Pagination
        const from = (page - 1) * limit;
        const to = from + limit - 1;
        query = query.range(from, to);

        // Exécution
        const result = await monitorQuery(
          tableName,
          'select',
          `paginated page=${page} limit=${limit}`,
          (() => query) as any,
          `useCRUD(${tableName}).fetchPaginated`
        );

        const { data: rawData, error: fetchError } = result;
        const count = (result as any).count;

        if (fetchError) throw fetchError;

        // Transformer les données si nécessaire
        let transformedData =
          transform && rawData
            ? (rawData as any[]).map((item: any) => transform(item))
            : (rawData as unknown as T[]) || [];

        // Appliquer le hook afterFetch si présent
        if (afterFetch) {
          transformedData = await afterFetch(transformedData);
        }

        const total = count || 0;
        const totalPages = Math.ceil(total / limit);

        return { data: transformedData, total, page, limit, totalPages };
      } catch (err) {
        handleError(err, 'fetchPaginated');
        return { data: [], total: 0, page: 1, limit: 10, totalPages: 0 };
      } finally {
        setLoading(false);
      }
    },
    [supabase, tableName, select, orderBy, transform, afterFetch, searchableFields, handleError]
  );

  /**
   * Récupérer une entité par son ID
   */
  const getById = useCallback(
    async (id: string): Promise<T | null> => {
      try {
        setError(null);

        // 📊 MONITORED: Récupération d'une entité par ID
        const { data: rawData, error: fetchError } = await monitorQuery(
          tableName,
          'select',
          `id=${id}`,
          (() => supabase.from(tableName).select(select).eq('id', id).single()) as any,
          `useCRUD(${tableName}).getById`
        );

        if (fetchError) {
          throw fetchError;
        }

        if (!rawData) {
          return null;
        }

        // Transformer les données si nécessaire
        const transformedData = transform ? transform(rawData as any) : (rawData as unknown as T);

        return transformedData;
      } catch (err) {
        handleError(err, 'getById');
        return null;
      }
    },
    [supabase, tableName, select, transform, handleError]
  );

  /**
   * Créer une nouvelle entité
   */
  const create = useCallback(
    async (input: TCreate): Promise<T | null> => {
      try {
        setError(null);

        // Transformer l'input si nécessaire
        const preparedInput = transformInput ? transformInput(input as Partial<T>) : input;

        // 📊 MONITORED: Création d'une entité
        const { data: rawData, error: createError } = await monitorQuery(
          tableName,
          'insert',
          'new entity',
          (() => supabase.from(tableName).insert([preparedInput]).select(select).single()) as any,
          `useCRUD(${tableName}).create`
        );

        if (createError) {
          throw createError;
        }

        if (!rawData) {
          throw new Error('Aucune donnée retournée après la création');
        }

        // Transformer les données
        const transformedData = transform ? transform(rawData as any) : (rawData as unknown as T);

        // Mise à jour optimiste du state local
        setData((prev) => [transformedData, ...prev]);

        return transformedData;
      } catch (err) {
        handleError(err, 'create');
        return null;
      }
    },
    [supabase, tableName, select, transform, transformInput, handleError]
  );

  /**
   * Mettre à jour une entité
   */
  const update = useCallback(
    async (id: string, input: TUpdate): Promise<boolean> => {
      try {
        setError(null);

        // 1. Récupérer les données actuelles pour le logging (si activé)
        let oldData: T | null = null;
        if (options.enableAutoLogging && options.entityType) {
          oldData = await getById(id);
        }

        // 2. Détecter si assigned_to a changé et récupérer les noms d'utilisateurs
        const inputObj = input as any;
        let assignedUserChanged = false;
        let oldUserId: string | null = null;
        let oldUserName: string | null = null;
        let newUserId: string | null = null;
        let newUserName: string | null = null;

        if (
          options.enableAutoLogging &&
          options.entityType &&
          oldData &&
          'assigned_to' in inputObj &&
          (oldData as any).assigned_to !== inputObj.assigned_to
        ) {
          assignedUserChanged = true;
          oldUserId = (oldData as any).assigned_to || null;
          newUserId = inputObj.assigned_to || null;

          // Les noms seront enrichis automatiquement par useTimeline via le cache
          // Pas besoin d'appeler get_users_list ici
          oldUserName = oldUserId;
          newUserName = newUserId;
        }

        // 3. Transformer l'input si nécessaire
        const preparedInput = transformInput ? transformInput(input as Partial<T>) : input;

        // 4. Faire l'update
        // 📊 MONITORED: Mise à jour d'une entité
        const { error: updateError } = await monitorQuery(
          tableName,
          'update',
          `id=${id}`,
          (() => supabase.from(tableName).update(preparedInput).eq('id', id)) as any,
          `useCRUD(${tableName}).update`
        );

        if (updateError) {
          throw updateError;
        }

        // 5. Logger le changement d'assignation si détecté
        if (assignedUserChanged && options.entityType) {
          await logAssignedUserChange(
            supabase,
            options.entityType,
            id,
            oldUserId,
            oldUserName,
            newUserId,
            newUserName,
            options.getUserId?.()
          );
        }

        // 6. Logger les autres changements automatiquement (en excluant assigned_to)
        if (options.enableAutoLogging && options.entityType && oldData) {
          // Ne merger que les champs qui sont réellement dans input
          const newData = { ...oldData, ...input };

          // Récupérer la liste des champs qui ont changé (clés de input), en excluant assigned_to
          const changedFields = Object.keys(input as object).filter((key) => key !== 'assigned_to');

          // Logger uniquement s'il reste des champs à logger
          if (changedFields.length > 0) {
            await logEntityUpdate(
              supabase,
              options.entityType,
              id,
              oldData,
              newData,
              options.getUserId?.(),
              changedFields
            );
          }
        }

        // 7. Mise à jour optimiste du state local
        setData((prev) =>
          prev.map((item) => (item.id === id ? { ...item, ...(input as Partial<T>) } : item))
        );

        return true;
      } catch (err) {
        handleError(err, 'update');
        return false;
      }
    },
    [supabase, tableName, transformInput, handleError, getById, options]
  );

  /**
   * Supprimer une entité
   */
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        setError(null);

        // 📊 MONITORED: Suppression d'une entité
        const { error: deleteError } = await monitorQuery(
          tableName,
          'delete',
          `id=${id}`,
          (() => supabase.from(tableName).delete().eq('id', id)) as any,
          `useCRUD(${tableName}).remove`
        );

        if (deleteError) {
          throw deleteError;
        }

        // Mise à jour optimiste du state local
        setData((prev) => prev.filter((item) => item.id !== id));

        return true;
      } catch (err) {
        handleError(err, 'remove');
        return false;
      }
    },
    [supabase, tableName, handleError]
  );

  /**
   * Supprimer plusieurs entités
   */
  const removeMany = useCallback(
    async (ids: string[]): Promise<boolean> => {
      try {
        setError(null);

        // 📊 MONITORED: Suppression multiple
        const { error: deleteError } = await monitorQuery(
          tableName,
          'delete',
          `${ids.length} entities`,
          (() => supabase.from(tableName).delete().in('id', ids)) as any,
          `useCRUD(${tableName}).removeMany`
        );

        if (deleteError) {
          throw deleteError;
        }

        // Mise à jour optimiste du state local
        setData((prev) => prev.filter((item) => !ids.includes(item.id)));

        return true;
      } catch (err) {
        handleError(err, 'removeMany');
        return false;
      }
    },
    [supabase, tableName, handleError]
  );

  /**
   * Effacer l'erreur
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Charger les données au montage (sauf si manual = true)
   */
  useEffect(() => {
    if (!manual) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    // État
    data,
    loading,
    error,

    // Lecture
    refresh,
    getById,
    fetchPaginated,

    // Écriture
    create,
    update,
    remove,
    removeMany,

    // Utilitaires
    setData,
    clearError,
  };
}
